import { HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

import {
  ManageVacationsCriteriaDto,
  EmployeeObservationsDto,
  VacationsSeasonDto,
  SeasonCalendarDto
} from '@app/data/dtos/manage-vacations/manage-vacations.dto';
import { MotManageVacationsProposalsDto } from '@app/data/dtos/manage-vacations/mot/mot-manage-vacations.dto';
import {
  mapManageVacationsCriteria,
  mapObservations,
  mapSeasonCalendar,
  mapVacationsSeason
} from '@app/data/mappers/manage-vacations/manage-vacations.mapper';
import {
  mapMotVacationsProposals,
  mapMotCreateProposalDto
} from '@app/data/mappers/manage-vacations/mot/mot-manage-vacations.mapper';
import { MotManageVacationsRepository } from '@app/domain/repositories/manage-vacations/mot/mot-manage-vacations.repository.contract';
import { ManageVacationsUserType, SEASONS } from '@app/entities/enums/manage-vacations/manage-vacations.enum';
import { ManageVacationsCriteria } from '@app/entities/models/manage-vacations/manage-vacations-criteria.model';
import {
  EmployeeObservations,
  SeasonCalendar,
  VacationsSeason
} from '@app/entities/models/manage-vacations/manage-vacations.model';
import {
  MotFactoryProposal,
  MotVacationsProposal
} from '@app/entities/models/manage-vacations/mot-manage-vacations.model';
import { TUserTypeCalendarProposal } from '@app/entities/types/manage-vacations.type';
import { ENV } from '@environments/environment';
import {
  SECTION_KEY,
  IGNORE_NO_CONTENT_ERROR
} from '@services/interceptors/error-sections.interceptor/error-sections.interceptor';

import { MRepository } from '@mercadona/core/utils/repository';

@Injectable({
  providedIn: 'root'
})
export class MotManageVacationsRepositoryImpl extends MRepository implements MotManageVacationsRepository {
  readonly motBaseUrl = '/mot/v3/vacations-proposal';

  constructor() {
    super(`${ENV.API_URL}`);
  }

  getProposals(): Observable<MotVacationsProposal> {
    return this.get<MotManageVacationsProposalsDto>(this.motBaseUrl, {
      context: new HttpContext().set(SECTION_KEY, 'manage-vacations').set(IGNORE_NO_CONTENT_ERROR, true)
    }).pipe(map(mapMotVacationsProposals));
  }

  getVacationsCriteria(): Observable<ManageVacationsCriteria> {
    return this.get<ManageVacationsCriteriaDto>(`${this.motBaseUrl}/criteria`, {
      context: new HttpContext().set(SECTION_KEY, 'manage-vacations')
    }).pipe(map(mapManageVacationsCriteria));
  }

  getVacationsSeason(): Observable<VacationsSeason> {
    return this.get<VacationsSeasonDto>(`${this.motBaseUrl}/seasons`, {
      context: new HttpContext().set(SECTION_KEY, 'manage-vacations')
    }).pipe(map(mapVacationsSeason));
  }

  getSeasonCalendar(season: SEASONS): Observable<SeasonCalendar<TUserTypeCalendarProposal>> {
    let params = new HttpParams();
    params = params.set('season', season);

    return this.get<SeasonCalendarDto>(`${this.motBaseUrl}/season-calendar`, {
      params,
      context: new HttpContext().set(SECTION_KEY, 'manage-vacations')
    }).pipe(map(mapSeasonCalendar(ManageVacationsUserType.MOT)));
  }

  createProposal(
    proposals: MotVacationsProposal,
    storedProposals: MotFactoryProposal[],
    isSummerSeason: boolean
  ): Observable<MotVacationsProposal> {
    const createProposal = mapMotCreateProposalDto(proposals, storedProposals, isSummerSeason);

    const request = {
      body: {
        ...createProposal
      }
    };

    return this.post<MotManageVacationsProposalsDto>(this.motBaseUrl, request).pipe(map(mapMotVacationsProposals));
  }

  updateObservations(observations: EmployeeObservations): Observable<EmployeeObservations> {
    const request = {
      body: {
        id: observations.id,
        content: observations.content
      }
    };

    return this.put<EmployeeObservationsDto>(`${this.motBaseUrl}/observations`, request).pipe(map(mapObservations));
  }
}
